import { ArrowRightOutlined, CheckCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Space, Steps, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MultipleImageUpload from '../../components/ui/MultipleImageUpload/MultipleImageUpload';
import TextToSpeech from '../../components/voice/TextToSpeech/TextToSpeech';
import VoiceRecorder from '../../components/voice/VoiceRecorder/VoiceRecorder';
import axios from '../../helpers/axiosConfig';
import { checkUserLogin } from '../../helpers/functions';
import googleAIService from '../../services/googleAIService';
import { initializeCreatePetition } from '../../store/petition/petitionSlice';
import './VoicePage.css';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

const VoicePage = () => {
  const { t } = useTranslation('global');
  const [currentStep, setCurrentStep] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [clientId, setClientId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedPetition, setGeneratedPetition] = useState(null);
  const [petitionImages, setPetitionImages] = useState([]);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Создаем анонимный client_id при загрузке страницы
    createAnonymousClient();
  }, []);

  const createAnonymousClient = async () => {
    // Если пользователь авторизован, client_id не нужен (бэкенд установит client_id = None)
    if (checkUserLogin()) {
      setClientId(null);
      return;
    }
    
    // Проверяем, есть ли сохраненный client_id в localStorage
    const savedClientId = localStorage.getItem('anonymous_client_id');
    if (savedClientId) {
      setClientId(savedClientId);
      return;
    }
    
    try {
      const response = await axios.post('/anonymous-client/create_client/');
      const clientIdValue = response.data.client_id;
      setClientId(clientIdValue);
      // Сохраняем client_id в localStorage для использования при удалении
      localStorage.setItem('anonymous_client_id', clientIdValue);
    } catch (error) {
      console.error('Error creating anonymous client:', error);
      // Fallback - создаем временный ID
      const tempClientId = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      setClientId(tempClientId);
      // Сохраняем временный ID в localStorage
      localStorage.setItem('anonymous_client_id', tempClientId);
      setError(t('voice.tempIdUsed'));
    }
  };

  const handleTranscriptReceived = async (receivedTranscript) => {
    // Просто сохраняем транскрипт, заголовок будет сгенерирован позже при создании инициативы
    setTranscript(receivedTranscript);
    setCurrentStep(1);
    setError(null);
  };

  const generatePetition = async () => {
    
    if (!transcript || !clientId) {
      setError(t('voice.missingTranscript'));
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      
      // Используем только frontend Google AI для генерации инициативы
      // Backend НЕ используется - вся обработка на клиенте
      const result = await googleAIService.generatePetitionFromVoice(
        transcript,
        'ru'
      );

      setGeneratedPetition(result);
      setCurrentStep(2);
      
    } catch (error) {
      console.error('❌ Ошибка генерации через Google AI:', error);
      
      // Показываем понятную ошибку пользователю
      const errorMessage = error.message || t('voice.generationFailed');
      setError(`❌ ${errorMessage}. ${t('voice.checkConnection')}`);
      message.error({
        content: `${t('voice.generationFailed')}. ${t('voice.tryAgain')}`,
        duration: 5
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createPetitionDirectly = async () => {
    if (!generatedPetition) return;
    
    // Проверяем наличие изображений
    if (!petitionImages || petitionImages.length === 0) {
      message.error(t('voice.imageRequired'));
      setError(`❌ ${t('voice.imageRequired')}`);
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      
      // Создаем FormData для отправки с изображениями
      const formData = new FormData();
      formData.append('title', generatedPetition.generated_content.title);
      formData.append('description', generatedPetition.generated_content.description);
      
      // Добавляем все изображения
      petitionImages.forEach((image) => {
        formData.append('images', image);
      });
      
      // Важно: передаем client_id для возможности удаления петиции
      if (clientId) {
        formData.append('client_id', clientId);
      } else {
        // Пытаемся получить из localStorage
        const savedClientId = localStorage.getItem('anonymous_client_id');
        if (savedClientId) {
          formData.append('client_id', savedClientId);
        }
      }
      
      const response = await axios.post('/voice-petition/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      
      // Показываем успешное сообщение с таймером
      message.success({
        content: (
          <div>
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              ✅ {t('voice.initiativeCreated')}
            </div>
            <div style={{ fontSize: 14 }}>
              <div>{t('voice.status')} <strong>{response.data.status}</strong></div>
              <div style={{ marginTop: 8, color: '#1890ff' }}>
                ⏱️ {t('voice.autoPublish')}
              </div>
            </div>
          </div>
        ),
        duration: 8,
        style: { marginTop: '10vh' }
      });
      
      // Переходим на страницу созданной инициативы
      setTimeout(() => {
        navigate(`/petition/${response.data.id}`);
      }, 2000);
      
    } catch (error) {
      console.error('Error creating petition:', error);
      
      if (error.response?.status === 429) {
        const retryMinutes = Math.ceil(error.response.data.retry_after / 60);
        message.error({
          content: `${t('voice.rateLimit')}. ${t('voice.rateLimitMinutes')} ${retryMinutes} ${t('voice.minutes')}.`,
          duration: 5
        });
        setError(`❌ ${t('voice.rateLimit')}.\n${t('voice.rateLimitMinutes')} ${retryMinutes} ${t('voice.minutes')}.`);
      } else if (error.response?.data) {
        // Форматируем ошибки валидации
        const errors = error.response.data;
        let errorMessages = [];
        
        // Обрабатываем каждое поле с ошибкой
        Object.keys(errors).forEach(field => {
          const fieldErrors = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
          const fieldName = {
            'image': t('voice.imageField'),
            'title': t('voice.titleField'),
            'description': t('voice.descriptionField'),
            'error': t('voice.errorField')
          }[field] || field;
          
          fieldErrors.forEach(err => {
            errorMessages.push(`${fieldName}: ${err}`);
          });
        });
        
        // Показываем все ошибки
        const errorText = errorMessages.join('\n');
        
        message.error({
          content: (
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                {t('voice.validationError')}
              </div>
              {errorMessages.map((msg, index) => (
                <div key={index} style={{ marginBottom: 4 }}>
                  • {msg}
                </div>
              ))}
            </div>
          ),
          duration: 8,
          style: { maxWidth: 500 }
        });
        
        setError(`❌ ${t('voice.validationError')}\n${errorText}`);
      } else {
        message.error(t('voice.createFailed'));
        setError(`❌ ${t('voice.createFailedTryLater')}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleImagesChange = (newImages) => {
    setPetitionImages(newImages);
  };

  const proceedToEdit = () => {
    if (generatedPetition) {
      // Инициализируем Redux state перед переходом
      dispatch(initializeCreatePetition({
        generatedContent: generatedPetition.generated_content,
        transcript: transcript,
        clientId: clientId,
        moderation: generatedPetition.moderation,
        images: petitionImages
      }));
      
      // Переходим на страницу создания инициативы
      navigate('/petitions/new');
    }
  };

  const steps = [
    {
      title: t('voice.step1Title'),
      description: t('voice.step1Desc'),
    },
    {
      title: t('voice.step2Title'),
      description: t('voice.step2Desc'),
    },
    {
      title: t('voice.step3Title'),
      description: t('voice.step3Desc'),
    },
  ];

  return (
    <div className="voice-page">
      <div className="voice-container">
        <div className="voice-header">
          <Title level={1}>{t('voice.voiceCreationTitle')}</Title>
          <Paragraph>
            {t('voice.voiceCreationDesc')}
          </Paragraph>
        </div>

        <Steps current={currentStep} className="voice-steps">
          {steps.map((step, index) => (
            <Step
              key={index}
              title={step.title}
              description={step.description}
              icon={currentStep > index ? <CheckCircleOutlined /> : null}
            />
          ))}
        </Steps>

        {error && (
          <Alert
            message={t('voice.error')}
            description={error}
            type="error"
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 24 }}
          />
        )}

        {currentStep === 0 && (
          <div role="main" aria-label="Запись голоса для создания инициативы">
            <Card 
              title={t('voice.recordingTitle')} 
              className="voice-recording-card"
              role="region"
              aria-label="Интерфейс записи голоса"
            >
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div 
                  className="recording-section"
                  role="region"
                  aria-label="Запись текста инициативы"
                  aria-live="polite"
                >
                  <Title level={4} id="description-section-heading">{t('voice.describeProblem')}</Title>
                  <Text type="secondary" id="description-section-description">
                    {t('voice.describeProblemDetailed')}
                  </Text>
                  <VoiceRecorder 
                    onTranscriptReceived={handleTranscriptReceived}
                    isLoading={isLoading}
                    placeholder={t('voice.describeProblemPlaceholder')}
                    aria-label="Запись текста инициативы"
                    aria-describedby="description-section-description"
                  />
                </div>

                <Alert
                  message={t('voice.tipsTitle')}
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>{t('voice.tip1')}</li>
                      <li>{t('voice.tip2')}</li>
                      <li>{t('voice.tip3')}</li>
                      <li>{t('voice.tip4')}</li>
                      <li>{t('voice.tip5')}</li>
                    </ul>
                  }
                  type="info"
                  showIcon
                  role="complementary"
                  aria-label="Советы для улучшения качества распознавания голоса"
                />
              </Space>
            </Card>
          </div>
        )}

        {currentStep === 1 && transcript && (
          <Card title={t('voice.transcriptTitle')} className="transcript-card">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Alert
                message={t('voice.transcriptRecorded')}
                description={t('voice.transcriptRecordedDesc')}
                type="success"
                showIcon
              />
              
              <div className="transcript-content">
                <div className="transcript-section">
                  <Text strong>{t('voice.yourVoiceMessage')}</Text>
                  <div className="transcript-text" style={{ marginTop: 8, padding: 16, background: '#f5f5f5', borderRadius: 4, fontSize: 15 }}>
                    "{transcript}"
                  </div>
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                    {t('voice.aiWillImprove')}
                  </Text>
                </div>
              </div>
              
              <div className="transcript-actions">
                <Space>
                  <Button 
                    type="primary" 
                    size="large"
                    icon={<ArrowRightOutlined />}
                    loading={isLoading}
                    onClick={generatePetition}
                  >
                    {t('voice.createInitiative')}
                  </Button>
                  <Button onClick={() => {
                    setCurrentStep(0);
                    setTranscript('');
                  }}>
                    {t('voice.recordAgain')}
                  </Button>
                </Space>
              </div>
            </Space>
          </Card>
        )}

        {currentStep === 2 && generatedPetition && (
          <Card title={t('voice.generatedInitiative')} className="generated-petition-card">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              
              {generatedPetition.moderation && !generatedPetition.moderation.is_safe && (
                <Alert
                  message={t('voice.attention')}
                  description={`${t('voice.potentialIssues')}: ${generatedPetition.moderation.issues.join(', ')}`}
                  type="warning"
                  showIcon
                />
              )}

              <div className="petition-preview">
                <div className="petition-title">
                  <Text strong>{t('voice.title')}</Text>
                  <Title level={3}>{generatedPetition.generated_content.title}</Title>
                </div>
                
                <div className="petition-description">
                  <Text strong>{t('voice.description')}</Text>
                  <div className="description-content">
                    {generatedPetition.generated_content.description}
                  </div>
                </div>

                <div className="petition-voice-controls" style={{ marginTop: '20px', marginBottom: '20px' }}>
                  <Text strong>{t('voice.listenInitiative')}</Text>
                  <div style={{ marginTop: '8px' }}>
                    <TextToSpeech 
                      text={generatedPetition.generated_content.description}
                      title={generatedPetition.generated_content.title}
                      size="middle"
                    />
                  </div>
                  <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                    {t('voice.listenBeforePublish')}
                  </Text>
                </div>

                <div className="petition-image" style={{ marginTop: '20px' }}>
                  <Text strong>
                    {t('voice.images')} <span style={{ color: '#ff4d4f' }}>*</span>
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                    {t('voice.imageRequired')}
                  </Text>
                  <div style={{ marginTop: '10px' }}>
                    <MultipleImageUpload
                      images={petitionImages}
                      onImagesChange={(newImages) => {
                        handleImagesChange(newImages);
                        // Очищаем ошибку при добавлении изображений
                        if (error && (error.includes('изображение') || error.includes('image') || error.includes('сүрөт')) && newImages.length > 0) {
                          setError(null);
                        }
                      }}
                      maxImages={10}
                      maxSizeMB={2}
                      acceptedTypes={['image/jpeg', 'image/png', 'image/webp']}
                    />
                  </div>
                  {(!petitionImages || petitionImages.length === 0) && error && (error.includes('изображение') || error.includes('image') || error.includes('сүрөт')) && (
                    <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                      {t('voice.imageRequired')}
                    </Text>
                  )}
                </div>
              </div>

              <div className="petition-actions">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Alert
                    message={t('voice.twoOptions')}
                    description={t('voice.twoOptionsDesc')}
                    type="info"
                    showIcon
                  />
                  
                  <Space wrap>
                    <Button 
                      type="primary" 
                      size="large"
                      icon={<SaveOutlined />}
                      onClick={createPetitionDirectly}
                      loading={isLoading}
                    >
                      ✅ {t('voice.publishNow')}
                    </Button>
                    <Button 
                      size="large"
                      icon={<ArrowRightOutlined />}
                      onClick={proceedToEdit}
                    >
                      ✏️ {t('voice.editBeforePublish')}
                    </Button>
                    <Button onClick={() => {
                      setCurrentStep(0);
                      setTranscript('');
                      setGeneratedPetition(null);
                      setPetitionImages([]);
                      setError(null);
                    }}>
                      🔄 {t('voice.regenerate')}
                    </Button>
                  </Space>
                </Space>
              </div>
            </Space>
          </Card>
        )}

      </div>
    </div>
  );
};

export default VoicePage;